import { ResourceClient, type HttpClient } from "@dl/web-core-js/api";

/** A place inside a warehouse (a Terrebonne shelf), with the lot waiting on it, if any. */
export interface StorageLocationDTO {
  id: number;
  warehouse_id: number;
  warehouse_code?: string | null;
  code: string;
  name: string | null;
  position: number;
  is_active: boolean;
  /** Computed: the packing or completed lot on it. */
  production_order_id?: number | null;
  production_order_number?: number | null;
  zone?: string | null;
  capacity_pallets?: number | null;
  note?: string | null;
  /** Computed: units on it, and of how many products. */
  shelved_units?: number | null;
  product_count?: number;
}

export class StorageLocationClient extends ResourceClient<StorageLocationDTO> {
  constructor(http: HttpClient) {
    super(http, "/manufacturing/storage-location");
  }

  /** Every active location of a warehouse, in shelf order. */
  forWarehouse(warehouseCode: string): Promise<StorageLocationDTO[]> {
    return this.find({
      page: 0,
      size: 500,
      // The repository's own order is warehouse, position, code.
      filters: { warehouse_code: [warehouseCode], is_active: ["true"] },
    }).then((page) => page.content);
  }
}

/** Units of a product on a shelf: placed by a lot's put-away or by hand, trimmed oldest first to the inventory. */
export interface StorageLocationStockDTO {
  id: number;
  storage_location_id: number;
  storage_location_code?: string | null;
  warehouse_code?: string | null;
  product_id: number;
  sku?: string | null;
  product_name?: string | null;
  quantity: number;
  /** The lot that placed them, when one did. */
  production_order_id?: number | null;
  production_order_number?: number | null;
  placed_date: string | null;
  note: string | null;
}

/** A product a warehouse holds more of than its shelves say. */
export interface UnshelvedProduct {
  product_id: number;
  sku: string;
  name: string | null;
  warehouse_id: number;
  warehouse_code: string;
  inventory: number;
  shelved: number;
  unshelved: number;
}

export class StorageLocationStockClient extends ResourceClient<StorageLocationStockDTO> {
  constructor(http: HttpClient) {
    super(http, "/manufacturing/storage-location-stock");
  }

  /** Everything on a warehouse's shelves, page after page. */
  async forWarehouse(warehouseCode: string): Promise<StorageLocationStockDTO[]> {
    const rows: StorageLocationStockDTO[] = [];
    for (let page = 0; ; page += 1) {
      const result = await this.find({ page, size: 1000, filters: { warehouse_code: [warehouseCode] } });
      rows.push(...result.content);
      if (result.content.length < 1000) return rows;
    }
  }

  unshelved(warehouseId?: number): Promise<UnshelvedProduct[]> {
    return this.http.get<UnshelvedProduct[]>(`${this.path}/unshelved`, { query: warehouseId == null ? {} : { warehouse_id: String(warehouseId) } });
  }
}
